/**
 * Platform detection utility for PWA installation
 */

export interface PlatformInfo {
  isIOS: boolean;
  isAndroid: boolean;
  isDesktop: boolean;
  isMobile: boolean;
  isStandalone: boolean;
  supportsInstallPrompt: boolean;
}

/**
 * Detects the user's platform and PWA support
 */
export const detectPlatform = (): PlatformInfo => {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  
  // Platform detection
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
  const isAndroid = /Android/.test(userAgent);
  const isMobile = isIOS || isAndroid;
  const isDesktop = !isMobile;
  
  // Installation status detection
  const isStandalone =
    (window.matchMedia('(display-mode: standalone)').matches) ||
    ((window.navigator as any).standalone === true);
  
  // Installation support detection
  const supportsInstallPrompt = 'BeforeInstallPromptEvent' in window;
  
  // Log platform detection results
  console.log('Platform detection:', {
    userAgent,
    isIOS,
    isAndroid,
    isDesktop,
    isMobile,
    isStandalone,
    supportsInstallPrompt
  });
  
  return {
    isIOS,
    isAndroid,
    isDesktop,
    isMobile,
    isStandalone,
    supportsInstallPrompt
  };
};

/**
 * Checks if the app is running in standalone mode (installed as PWA)
 */
export const isAppInstalled = (): boolean => {
  return detectPlatform().isStandalone;
};

/**
 * Checks if the browser supports PWA installation
 */
export const supportsInstallation = (): boolean => {
  return detectPlatform().supportsInstallPrompt;
};

/**
 * Gets platform-specific installation instructions
 */
export const getInstallInstructions = (): string => {
  const { isIOS, isAndroid, isDesktop } = detectPlatform();
  
  if (isIOS) {
    return "Tap the share icon, then 'Add to Home Screen'";
  } else if (isAndroid) {
    return "Tap 'Install' to add VeriPay to your home screen";
  } else if (isDesktop) {
    return "Click the install icon in your browser's address bar";
  }
  
  return "Add VeriPay to your home screen for faster access";
};